import { supabase } from '../lib/supabase';
import { checkApplicant } from './applicants';

export const createTeam = async (formData) => {
  try {
    const { team_name, member_emails, idea } = formData;

    // Check if team name already exists
    const { data: existingTeam } = await supabase
      .from('teams')
      .select('id')
      .eq('team_name', team_name)
      .single();

    if (existingTeam) {
      return {
        success: false,
        message: 'اسم الفريق مستخدم مسبقًا'
      };
    }

    // Validate all members and get the first one as leader
    if (!member_emails || member_emails.length === 0) {
      return {
        success: false,
        message: 'يجب إضافة عضو واحد على الأقل'
      };
    }

    const memberIds = [];
    let leaderId = null;

    for (let i = 0; i < member_emails.length; i++) {
      const email = member_emails[i];
      const { data: member, error: memberError } = await checkApplicant(email);
      if (memberError || !member) {
        return {
          success: false,
          message: `لم يتم العثور على العضو ${email} في قاعدة البيانات`
        };
      }

      if (member.status !== 'مقبول') {
        return {
          success: false,
          message: `العضو ${email} غير مقبول بعد`
        };
      }

      // First member becomes the leader
      if (i === 0) {
        leaderId = member.id;

        // Check if first member already has a team as leader
        const { data: existingLeaderTeam } = await supabase
          .from('teams')
          .select('id')
          .eq('leader_id', member.id)
          .single();

        if (existingLeaderTeam) {
          return {
            success: false,
            message: `العضو ${email} مسجل كقائد لفريق آخر مسبقًا`
          };
        }
      }

      memberIds.push(member.id);
    }

    // Create team with first member as leader
    const { data: team, error: teamError } = await supabase
      .from('teams')
      .insert([{
        team_name,
        leader_id: leaderId,
        idea,
        status: 'قيد المراجعة'
      }])
      .select()
      .single();

    if (teamError) {
      throw teamError;
    }

    // Add all team members (including leader)
    const teamMembers = memberIds.map(member_id => ({
      team_id: team.id,
      applicant_id: member_id
    }));

    const { error: membersError } = await supabase
      .from('team_members')
      .insert(teamMembers);

    if (membersError) {
      // Rollback team creation
      await supabase.from('teams').delete().eq('id', team.id);
      throw membersError;
    }

    return {
      success: true,
      message: 'تم إنشاء الفريق بنجاح!',
      data: team
    };
  } catch (err) {
    console.error('Error creating team:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء إنشاء الفريق. يرجى المحاولة مرة أخرى.'
    };
  }
};

export const getAllTeams = async (filters = {}) => {
  try {
    let query = supabase
      .from('teams')
      .select(`
        *,
        leader:applicants!teams_leader_id_fkey(full_name, email),
        team_members(
          applicant:applicants(full_name, email)
        )
      `)
      .order('created_at', { ascending: false });

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  } catch (err) {
    console.error('Error fetching teams:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء جلب بيانات الفرق',
      data: []
    };
  }
};

export const updateTeamStatus = async (teamId, status) => {
  try {
    const { data, error } = await supabase
      .from('teams')
      .update({ status })
      .eq('id', teamId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      success: true,
      message: `تم تحديث حالة الفريق إلى ${status}`,
      data
    };
  } catch (err) {
    console.error('Error updating team status:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء تحديث حالة الفريق'
    };
  }
};

export const deleteTeam = async (teamId) => {
  try {
    // Delete team members first
    const { error: membersError } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId);

    if (membersError) {
      throw membersError;
    }

    // Delete team
    const { error: teamError } = await supabase
      .from('teams')
      .delete()
      .eq('id', teamId);

    if (teamError) {
      throw teamError;
    }

    return {
      success: true,
      message: 'تم حذف الفريق بنجاح'
    };
  } catch (err) {
    console.error('Error deleting team:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء حذف الفريق'
    };
  }
};

export const getTeamStats = async () => {
  try {
    const { data, error } = await supabase
      .from('teams')
      .select('status');

    if (error) {
      throw error;
    }

    const stats = {
      total: data.length,
      approved: data.filter(t => t.status === 'مقبول').length,
      rejected: data.filter(t => t.status === 'مرفوض').length,
      pending: data.filter(t => t.status === 'قيد المراجعة').length
    };

    return {
      success: true,
      data: stats
    };
  } catch (err) {
    console.error('Error fetching team stats:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء جلب إحصائيات الفرق',
      data: null
    };
  }
};
